import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from "react-router-dom";
import { Group, Text, Tooltip, Card, Table, Space, Collapse, Badge, useMantineTheme } from '@mantine/core';
import { IconChartBar } from '@tabler/icons-react';
import { SectionHeader } from '../Report/shared';
import { STATUS, withAlpha } from '../../utils/statusTokens';
import { IconHelp } from '@tabler/icons-react';
import ReactDOM from 'react-dom';
import TestsApi from "../../api/TestsApi";
import { SelectTooltip } from "../../components/GeneralComponent/GeneralComponent";

export function Variables({ pageId, showVariablesTab, selectedTimePicker, selectedTimePickerFromDate, selectedTimePickerToDate, systemUsageLoader, tabRow, testName, goalLabel }:any) {
  const navigate = useNavigate();

  const [variablesMessageShow, setVariablesMessageShow] = useState('');
  const [variablesOptimumResponse, setVariablesOptimumResponse] = useState('');
  const [variablesVariablesTab, setVariablesVariablesTab] = useState([]);
  // Default open and not persisted, matching the Test Review sections.
  const [influenceOpen, setInfluenceOpen] = useState(true);
  const theme = useMantineTheme();
  const isDark = theme.colorScheme === 'dark';
  // Same header treatment as the variations table: small, uppercase,
  // letter-spaced, grey - one rule for every column.
  const newTableSx = {
    'thead th': {
      textTransform: 'uppercase' as const,
      fontSize: 11,
      letterSpacing: 0,
      fontWeight: 500,
      color: '#868e96',
    },
  };
  const dateRangeLabel = Number(selectedTimePicker) === -1
    ? 'All Time'
    : [selectedTimePickerFromDate, selectedTimePickerToDate].filter(Boolean).join(' - ');

  const variablesRef = useRef(null);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [helpTargetId, setHelpTargetId] = useState(0);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if(showVariablesTab){
      systemUsageLoader(true);
      TestsApi.getvariablestab({
        page_id: pageId,
        time: selectedTimePicker,
        from: selectedTimePickerFromDate,
        to: selectedTimePickerToDate
      })
      .then((response) => {
        if (response.ok) {
          return response.json();
        } else {
          if (response.status === 401 || response.status === 400) {
            navigate('/logout');
          }
        }
      })
      .then((data) => {
        if (data.status === 1) {
          setVariablesMessageShow(data.data.message_show);
          setVariablesOptimumResponse(data.data.optimum_response);
          setVariablesVariablesTab(data.data.variables_tab || []);
          systemUsageLoader(false);
        }
      })
      .catch((error) => {
        console.error('API Error:', error);
        systemUsageLoader(false);
      });
    }
  },[showVariablesTab]);

  // The API emits one 'variable' row followed by its 'version' rows, so one
  // pass rebuilds the hierarchy. Previously the only cue that a variable row
  // differed from its versions was a 20px indent.
  const variableGroups = useMemo(() => {
    const groups: any[] = [];
    (variablesVariablesTab as any[]).forEach((row: any) => {
      if (row?.type === 'version' && groups.length > 0) {
        groups[groups.length - 1].versions.push(row);
      } else {
        groups.push({ variable: row, versions: [] });
      }
    });
    return groups;
  }, [variablesVariablesTab]);

  /* ---------------------------------------------------------------------
     Variable Influence visual scale - Variables tab ONLY.
     Statistical Significance is untouched: it still renders through
     get_progress_class() and the shared .pp-0/.pp-25/.pp-50/.pp-75 classes.

     Influence is a normalised share of a 100% pool, so a fixed percentage
     threshold means something different in every test. The tier is therefore
     taken from the equal-share baseline (100 / qualifying variables), and the
     bar is rescaled against the strongest qualifying variable so the leader
     always reads as full width. Neither changes the percentage itself.

     One green token, only its intensity varies - every variable showing a bar
     already beat the median, so red or amber would report a failure where
     there is none. Dark surfaces wash out low alphas, so the dark ramp is
     lifted rather than reused.
     --------------------------------------------------------------------- */
  const influenceAlpha = isDark ? [1, 0.75, 0.55] : [1, 0.65, 0.4];

  const influenceValues = (variablesVariablesTab as any[])
    .filter((e) => typeof e.statistical_significance === 'string' && e.statistical_significance.indexOf('%') > -1)
    .map((e) => parseFloat(e.statistical_significance))
    .filter((n) => !isNaN(n) && n > 0);

  const highestInfluence = influenceValues.length ? Math.max(...influenceValues) : 0;
  const equalShareBaseline = influenceValues.length ? 100 / influenceValues.length : 0;

  const influenceColor = (value: number) => {
    const ratio = equalShareBaseline > 0 ? value / equalShareBaseline : 0;
    const alpha = ratio >= 1.3 ? influenceAlpha[0] : (ratio >= 0.8 ? influenceAlpha[1] : influenceAlpha[2]);
    return withAlpha(STATUS.good, alpha);
  };

  /* bar_width = variable_influence / highest_qualifying_influence * 100 */
  const influenceWidth = (value: number) =>
    highestInfluence > 0 ? Math.max(0, Math.min(100, (value / highestInfluence) * 100)) : 0;

  const trackBg = isDark ? theme.colors.dark[4] : theme.colors.gray[2];
  const groupBg = isDark ? theme.colors.dark[5] : theme.colors.gray[1];

  const newVariableRows = variableGroups.map((g: any, gi: number) => {
    const v = g.variable || {};
    const best = g.versions.find((x: any) => x.best_option === 'Best Option!');
    const pct = parseFloat(String(v.statistical_significance || '').replace('%', ''));
    const hasBar = !Number.isNaN(pct) && pct > 0;
    return (
      <React.Fragment key={`vg-${gi}`}>
        <tr style={{ backgroundColor: groupBg }}>
          <td>
            <Tooltip label={v.variables_name_complete || v.variables_name || ''}>
              <Text size="sm" fw={600}>{v.variables_name}</Text>
            </Tooltip>
          </td>
          <td><Text size="sm">{g.versions.length}</Text></td>
          <td><Text size="sm">{best ? best.versions_name : '-'}</Text></td>
          <td>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Text fw={500} size="sm" style={{ width: 42 }}>{v.statistical_significance}</Text>
              {hasBar && (
                <div
                  role="img"
                  aria-label={`Variable influence ${v.statistical_significance}`}
                  style={{ flex: 1, minWidth: 80, height: 10, backgroundColor: trackBg, borderRadius: 5, overflow: 'hidden' }}
                >
                  <div style={{ width: `${influenceWidth(pct)}%`, height: '100%', backgroundColor: influenceColor(pct), borderRadius: 5 }} />
                </div>
              )}
            </div>
          </td>
          <td />
        </tr>
        {g.versions.map((ver: any, vi: number) => (
          <tr key={`vg-${gi}-v-${vi}`}>
            <td><Text size="sm" ml={20}>{ver.versions_name}</Text></td>
            <td /><td /><td />
            <td>
              {ver.best_option === 'Best Option!' && (
                <Badge color="green" variant="light" size="sm" sx={{ textTransform: 'none' }}>
                  Best Option
                </Badge>
              )}
            </td>
          </tr>
        ))}
      </React.Fragment>
    );
  });


  const handleHelpClick = (id:any) => {
    setShowHelpModal(true);
    setHelpTargetId(id);
    if (variablesRef.current) {
      const { top, left, width } = variablesRef.current.getBoundingClientRect();
      const scrollOffset = window.scrollY;
      setTooltipPosition({
        top: top + scrollOffset,
        left: left + width + 10, 
      });
    }
  };

  return (
    <>
      {showHelpModal &&
        ReactDOM.createPortal(
        <div>
          <SelectTooltip
            isVisible={showHelpModal}
            setIsVisible={setShowHelpModal}
            id={helpTargetId}
            tooltipPosition={tooltipPosition}
          />
        </div>,
        document.body
      )}
      <Card shadow="sm" padding="md" radius="md" withBorder mt={10} mb={20}>
        {tabRow}
        <Text weight={500} mt={10} c="red">{variablesMessageShow}</Text>
        {/* The repeat-experiment NOTE is gone: the feature was removed, so the
            line described something customers can no longer do. */}
        <>
            <SectionHeader
              icon={<IconChartBar size="1rem" color="#0ea5e9" />}
              label="Variable Influence"
              rightNode={goalLabel ? (
                <Badge color="blue" variant="outline" size="sm" sx={{ textTransform: 'none' }}>
                  {goalLabel}
                </Badge>
              ) : undefined}
              opened={influenceOpen}
              toggle={() => setInfluenceOpen((o) => !o)}
            />
            <Collapse in={influenceOpen}>
              {/* Orientation line, matching "Primary Goal:" on the Aggregated Report. */}
              <Text size="xs" color="dimmed" mt={10}>
                {[testName, goalLabel, dateRangeLabel].filter(Boolean).join('  ·  ')}
              </Text>
              <Table highlightOnHover my={15} fontSize="xs" sx={newTableSx}>
                <thead>
                  <tr>
                    <th>Variable / Version</th>
                    <th>Versions</th>
                    <th>Best version</th>
                    <th>
                      Variable influence{' '}
                      <Tooltip
                        multiline
                        width={280}
                        withArrow
                        label="How much this variable moved the result, as a share of 100 across the variables that qualified. Only variables scoring above the median qualify, so every bar shown beat at least half the field."
                      >
                        <span style={{ cursor: 'pointer' }}>&#9432;</span>
                      </Tooltip>
                    </th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>{newVariableRows}</tbody>
              </Table>
            </Collapse>
        </>
      </Card>
      <Card shadow="sm" padding="sm" radius="md" withBorder mt={10} mb={20}>
        <Group position="apart" mb="xs">
          <Text weight={500}>OPTIMUM EXPECTED CONVERSION RATE <IconHelp ref={variablesRef} size="1.1rem" color="red" style={{marginBottom:-3, cursor: 'pointer'}} onClick={() => handleHelpClick(288)}/></Text>
        </Group>
        <Text weight={500} mt={20}>Optimum Expected Conversion Rate: {variablesOptimumResponse}%</Text>
        <Space h="xs" />
        <Text weight={500}>How To Use This Data:</Text>
        <Text>The above chart details which variables had the most influence on making a conversion. Above you will find the best variable options to use.Create a new page using the above influential variables.</Text>
        <Space h="xs" />
        <Text weight={500}>What about the Non-Influential Variables?</Text>
        <Text>You can not ignore them.</Text>
        <Text>Choose the option that is least expensive or most convenient. In most cases we recommend choosing the control version of the variable for non-influential variables.</Text>
        <Space h="xs" />
        <Text weight={500}>Optimum Expected Conversion Rate</Text>
        <Text>This is the estimated best expected conversion rate if you create a new page version using the data as described.</Text>
        <Text>Please note this is not a guarantee of a specific conversion rate as there are too many other factors in the marketplace that affect conversion.</Text>
        <Text>We highly recommend you test this new page against the best page from your last test.</Text>
      </Card>
    </>
  );
}